import { memo } from 'react';
import Link from 'next/link';

import { Box, PropertyCard as ConstellationPropertyCard, Icon, IconButton } from '@zillow/constellation-10';
import { IconChevronLeftFilled, IconWarningFilled } from '@zillow/constellation-icons';

import { LoadingTag } from '@zg-rentals/loading-components';
import {
  useRentalManagerUserPropertyDetailsWebApiHandler_overview,
  useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { useScreenSize } from '../../../hooks/useScreenSize';
import { getTagTypeByKey, STATUS_KEY } from '../../../utils/statusTagUtils';

import { ROUTES } from '../../../constants/urls';

import { backLinkStyles, propertyPhotoContainerStyles } from './styles';

import { useAuth } from '../../AuthProvider';
import AddressDetails from '../AddressDetails';
import EditPropertyTypeMenu from '../EditPropertyTypeMenu';

import type { CachedPropertyAddress } from '../../../utils/navigationCacheUtils';

type Props = {
  listingAlias: string;
  fallbackAddress?: CachedPropertyAddress;
};

const PropertyCard = ({ listingAlias, fallbackAddress }: Props) => {
  const { isScreenDesktop } = useScreenSize();
  const tagSize = isScreenDesktop ? 'md' : 'sm';
  const { isAuthorized } = useAuth();

  const { data: propertyDetailsOverviewData, isLoading: isPropertyDetailsOverviewLoading } =
    useRentalManagerUserPropertyDetailsWebApiHandler_overview(
      { listingAlias },
      { enabled: Boolean(listingAlias) && isAuthorized },
    );

  const isShowingFallback = !propertyDetailsOverviewData && Boolean(fallbackAddress);

  useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get(
    {
      listingAlias,
      includeLeases: false,
    },
    { enabled: Boolean(listingAlias) && isAuthorized },
  );

  return (
    <div data-testid="DashboardPropertyCard">
      <Box css={{ display: 'flex', justifyContent: { base: 'space-between', lg: 'start' }, alignItems: 'center' }}>
        <Box css={{ display: 'flex', justifyContent: { base: 'space-between', lg: 'start' }, alignItems: 'center' }}>
          <IconButton
            asChild
            css={backLinkStyles}
            emphasis="outlined"
            shape="circle"
            icon={<IconChevronLeftFilled />}
            size="sm"
            title="Back to properties"
          >
            <Link href={{ pathname: ROUTES.PROPERTIES }} />
          </IconButton>
          <Box
            data-loading={isPropertyDetailsOverviewLoading || null}
            css={{ overflow: 'hidden', ...propertyPhotoContainerStyles }}
          >
            {!isPropertyDetailsOverviewLoading && (
              <ConstellationPropertyCard.Photo
                src={propertyDetailsOverviewData?.photoUrl}
                alt={propertyDetailsOverviewData?.photoUrl ? 'property' : 'house'}
                fallbackImg={<ConstellationPropertyCard.PhotoFallback />}
                css={{ width: '100%', height: '100%' }}
              />
            )}
          </Box>
          <Box
            css={{
              display: 'flex',
              flexDirection: { base: 'column', lg: 'row' },
              alignSelf: { base: 'flex-start', lg: 'center' },
            }}
          >
            <AddressDetails address={propertyDetailsOverviewData?.address ?? fallbackAddress} />
            <Box
              css={{
                alignSelf: 'start',
                lg: { marginTop: '300' },
                xl: { marginTop: '100' },
              }}
            >
              <LoadingTag
                size={tagSize}
                tone={
                  isPropertyDetailsOverviewLoading || isShowingFallback
                    ? 'gray'
                    : getTagTypeByKey(STATUS_KEY?.PROPERTY_DETAILS_OVERVIEW, propertyDetailsOverviewData?.status ?? '')
                }
                icon={
                  Boolean(propertyDetailsOverviewData?.status === 'UNABLE TO PUBLISH') && (
                    <Icon size="md" css={{ color: 'icon.feedback.notify.hero' }} render={<IconWarningFilled />} />
                  )
                }
                isLoading={isPropertyDetailsOverviewLoading || isShowingFallback}
                loadingWidth="5rem"
              >
                {propertyDetailsOverviewData?.status}
              </LoadingTag>
            </Box>
          </Box>
        </Box>
        <Box css={{ alignSelf: 'start', marginLeft: '200', lg: { marginLeft: '400', paddingTop: '18px' } }}>
          <EditPropertyTypeMenu listingAlias={listingAlias} />
        </Box>
      </Box>
    </div>
  );
};

export default memo(PropertyCard);
